import { Link } from "react-router-dom";

import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { EntityIcon } from "@/components/ui/icons";
import { getEntityColor } from "@/components/UnifiedRegistry/utils";
import { cn } from "@/lib/utils";
import { EntityType } from "@/queries";

type EntityLinkSize = "xs" | "sm" | "md";

interface EntityLinkProps {
  type: EntityType;
  name: string;
  version?: number;
  link?: string;
  className?: string;
  size?: EntityLinkSize;
}

const getSizeClasses = (size: EntityLinkSize) => {
  switch (size) {
    case "xs":
      return {
        container: "gap-1 px-1 py-0",
        iconWrapper: "p-1",
        text: "text-xs",
      };
    case "sm":
      return {
        container: "gap-1 px-1.5 py-0.5",
        iconWrapper: "p-1.5",
        text: "text-xs",
      };
    case "md":
    default:
      return {
        container: "gap-1 px-2 py-1",
        iconWrapper: "p-2",
        text: "text-xs",
      };
  }
};

const EntityLink = ({ type, name, version, link, className, size = "md" }: EntityLinkProps) => {
  const sizeClasses = getSizeClasses(size);

  const content = (
    <>
      <div
        className={cn("flex-shrink-0 rounded-full", sizeClasses.iconWrapper, getEntityColor(type))}
      >
        <EntityIcon type={type} size="xs" />
      </div>
      <div className="flex min-w-0 flex-1 flex-row gap-2">
        <OverflowDisplayTooltip content={name}>
          <span
            className={cn("truncate whitespace-nowrap font-medium text-gray-900", sizeClasses.text)}
          >
            {name}
          </span>
        </OverflowDisplayTooltip>
        {version !== undefined && (
          <span
            className={cn(
              sizeClasses.text,
              version === -1 ? "italic text-orange-600" : "text-gray-500",
            )}
          >
            {version === -1 ? "Draft" : `Version #${version}`}
          </span>
        )}
      </div>
    </>
  );

  const baseClassName = cn(
    "inline-flex min-w-0 max-w-full items-center rounded-md border border-gray-300 bg-white",
    sizeClasses.container,
    link && "transition-colors hover:bg-gray-50",
    className,
  );

  if (link) {
    return (
      <Link to={link} className={baseClassName}>
        {content}
      </Link>
    );
  }

  return <div className={baseClassName}>{content}</div>;
};

export default EntityLink;
